import React, { useState, useEffect } from "react";
import {
  Form,
  Button,
  Container,
  Row,
  Col,
  Alert,
  Spinner,
  Card,
  Table,
  Image,
  Modal,
} from "react-bootstrap";
import axios from "axios";

const BASE_URL = "https://drdprints.com/api"; // Define the base URL for your API

const SubCategoryManager = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState({}); // Holds only the cat_id
  const [subCatTitle, setSubCatTitle] = useState("");
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [message, setMessage] = useState("");
  const [variant, setVariant] = useState("success");
  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSubCategory, setEditSubCategory] = useState(null);
  const [subCategories, setSubCategories] = useState([]); // Initialize as an empty array
  const [mainSubCategories, setMainSubCategories] = useState([]);
  const [selectedMainSubCategory, setSelectedMainSubCategory] = useState("");

  // Fetch main subcategories
  const fetchMainSubCategories = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/get-sub-main-category`);
      if (response.status === 200 && response.data && response.data.sub_main_categories) {
        setMainSubCategories(response.data.sub_main_categories); // Set the main subcategories
      } else {
        setMessage('Failed to load main subcategories.');
        setVariant('danger');
      }
    } catch (error) {
      console.error('Error fetching main subcategories:', error);
      setMessage('Failed to load main subcategories.');
      setVariant('danger');
    }
  };

  useEffect(() => {
    fetchMainSubCategories();
  }, []);

  // Fetch all categories on component mount
  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/get-category`);
      if (response.status === 200 && response.data && response.data.categories) {
        setCategories(response.data.categories);
      } else {
        setMessage("Failed to load categories.");
        setVariant("danger");
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setMessage("Failed to load categories.");
      setVariant("danger");
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Fetch subcategories by category slug
  const fetchSubCategories = async (slug) => {
    try {
      const response = await axios.get(`https://drdprints.com${slug}`);
      if (response.status === 200 && response.data && response.data.categories) {
        setSubCategories(response.data.categories[0].sub_categories || []);
      } else {
        setMessage("Failed to load subcategories.");
        setVariant("danger");
      }
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      setMessage("Failed to load subcategories.");
      setVariant("danger");
    }
  };


  // Handle category selection and fetching subcategories
  useEffect(() => {
    if (selectedCategory) {
      const category = categories.find((cat) => cat.id === parseInt(selectedCategory));
      if (category && category.slug) {
        fetchSubCategories(category.slug); // Fetch subcategories using the slug
      }
    } else {
      setSubCategories([]); // Clear subcategories when no category is selected
    }
  }, [selectedCategory, categories]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setImage(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    if (file) {
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  const handleCategoryChange = (e) => {
    const selectedCatId = e.target.value;
    setSelectedCategory(selectedCatId); // Set the selected category ID
  };

  const handleAddSubCategory = async () => {
    setLoading(true);

    if (!selectedCategory || !subCatTitle || !selectedMainSubCategory) {
      setMessage('Category ID, Sub Category Title, and Main Subcategory are required.');
      setVariant('danger');
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append('cat_id', selectedCategory); // Pass the selectedCategory directly
    formData.append('cat_sub_title', subCatTitle);
    formData.append('main_cat_id', selectedMainSubCategory); // Pass the selected main subcategory

    if (image) {
      formData.append('image', image);
    }

    try {
      const response = await axios.post(`${BASE_URL}/store-sub-category`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.status === 200) {
        setMessage('Sub-category added successfully.');
        setVariant('success');
        // Clear form fields after successful submission
        setSelectedCategory('');
        setSubCatTitle('');
        setSelectedMainSubCategory(''); // Clear main subcategory selection
        setImage(null);
        setImagePreview(null);
        fetchSubCategories(); // Refresh the subcategories after adding
      } else {
        setMessage('Failed to add sub-category.');
        setVariant('danger');
      }
    } catch (error) {
      console.error('Error adding sub-category:', error);
      setMessage('An error occurred while adding the sub-category.');
      setVariant('danger');
    }

    setLoading(false);
  };

  const handleEditClick = (subCategory) => {
    setEditSubCategory(subCategory);
    setSubCatTitle(subCategory.cat_sub_title);
    setImagePreview(subCategory.image);
    setShowEditModal(true);
  };

  const handleUpdateSubCategory = async () => {
    setLoading(true);

    const formData = new FormData();
    formData.append("cat_id", selectedCategory); // Ensure the correct cat_id is included
    formData.append("cat_sub_title", subCatTitle);

    if (image) {
      formData.append("image", image);
    }

    try {
      const response = await axios.put(
        `${BASE_URL}/update-category/${editSubCategory.id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200) {
        setMessage("Subcategory updated successfully.");
        setVariant("success");
        setShowEditModal(false);
        const category = categories.find((cat) => cat.id === parseInt(selectedCategory));
        fetchSubCategories(category.slug); // Refresh the subcategories after updating
      } else {
        setMessage("Failed to update subcategory.");
        setVariant("danger");
      }
    } catch (error) {
      console.error("Error updating subcategory:", error);
      setMessage("An error occurred while updating the subcategory.");
      setVariant("danger");
    }

    setLoading(false);
  };

  const handleDeleteSubCategory = async (subCategoryId) => {
    try {
      const response = await axios.delete(`${BASE_URL}/deletesubcategory/${subCategoryId}`);

      if (response.status === 200) {
        setMessage("Subcategory deleted successfully.");
        setVariant("success");
        const category = categories.find((cat) => cat.id === parseInt(selectedCategory));
        fetchSubCategories(category.slug); // Refresh the subcategories after deleting
      } else {
        setMessage("Failed to delete subcategory.");
        setVariant("danger");
      }
    } catch (error) {
      console.error("Error deleting subcategory:", error);
      setMessage("An error occurred while deleting the subcategory.");
      setVariant("danger");
    }
  };

  return (
    <Container>
      <Row className="mb-4 mt-4">
        <Col xs={12} md={6}>
          <Form>
            <Form.Group controlId="formCategorySelect">
              <Form.Label>Select Category</Form.Label>
              <Form.Control
                as="select"
                value={selectedCategory}
                onChange={handleCategoryChange} // Handle category change to set the ID
              >
                <option value="">Select a category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.cat_title}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group controlId="formMainSubCategorySelect">
              <Form.Label>Select Main Subcategory</Form.Label>
              <Form.Control
                as="select"
                value={selectedMainSubCategory}
                onChange={(e) => setSelectedMainSubCategory(e.target.value)} // Ensure correct subcategory id is being selected
              >
                <option value="">Select a main subcategory</option>
                {mainSubCategories.map((mainSubCat) => (
                  <option key={mainSubCat.id} value={mainSubCat.id}>
                    {mainSubCat.sub_main_title} {/* Use sub_main_title */}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group className="mt-3" controlId="formImageUpload">
              <Form.Label>Upload Image</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleFileChange}
              />
              {imagePreview && (
                <Image
                  src={imagePreview}
                  thumbnail
                  style={{ maxWidth: "200px", marginTop: "10px" }}
                />
              )}
            </Form.Group>
          </Form>
        </Col>

        <Col xs={12} md={6}>
          <Form.Group controlId="formSubCategoryTitle">
            <Form.Label>Subcategory Title</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter subcategory title"
              value={subCatTitle}
              onChange={(e) => setSubCatTitle(e.target.value)}
            />
          </Form.Group>

          <Button
            className="mt-5"
            variant="primary"
            onClick={handleAddSubCategory}
            disabled={loading}
            block
          >
            {loading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Add Subcategory"
            )}
          </Button>

          {message && (
            <Alert variant={variant} className="mt-3">
              {message}
            </Alert>
          )}
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <Card>
            <Card.Header>Categories and Subcategories</Card.Header>
            <Card.Body>
              {subCategories && subCategories.length > 0 ? (
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Subcategory Title</th>
                      <th>Subcategory Image</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subCategories.map((subCat, index) => (
                      <tr key={subCat.id}>
                        <td>{index + 1}</td>
                        <td>{subCat.cat_sub_title}</td>
                        <td>
                          {subCat.image && (
                            <Image
                              src={subCat.image}
                              thumbnail
                              style={{ maxWidth: "100px" }}
                            />
                          )}
                        </td>
                        <td>
                          <Button
                            variant="warning"
                            size="sm"
                            onClick={() => handleEditClick(subCat)}
                          >
                            Edit
                          </Button>{" "}
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDeleteSubCategory(subCat.id)}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p>No subcategories found.</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Edit Subcategory Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Subcategory</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="formEditSubCategoryTitle">
              <Form.Label>Subcategory Title</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter subcategory title"
                value={subCatTitle}
                onChange={(e) => setSubCatTitle(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mt-3" controlId="formEditImageUpload">
              <Form.Label>Upload Image</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleFileChange}
              />
              {imagePreview && (
                <Image
                  src={imagePreview}
                  thumbnail
                  style={{ maxWidth: "200px", marginTop: "10px" }}
                />
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateSubCategory}
            disabled={loading}
          >
            {loading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Update Subcategory"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default SubCategoryManager;
